import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Plus, Check, Search } from 'lucide-react';

/**
 * "Add widget" panel. Lists ONLY what the server said this user may see —
 * a restricted widget is absent, never greyed out, because advertising the
 * existence of data someone cannot have is itself a leak.
 */
export const WidgetPicker = ({ open, onOpenChange, catalog, categories, placedKeys, onAdd }) => {
  const [query, setQuery] = useState('');

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = catalog.filter((w) =>
      !q || w.title.toLowerCase().includes(q) || (w.description || '').toLowerCase().includes(q));
    const order = categories?.length ? categories : [...new Set(matches.map((w) => w.category))];
    return order
      .map((cat) => ({ cat, items: matches.filter((w) => w.category === cat) }))
      .filter((g) => g.items.length);
  }, [catalog, categories, query]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="widget-picker">
        <DialogHeader>
          <DialogTitle>Add a widget</DialogTitle>
          <DialogDescription>
            Everything here is available to you. Anything you don&apos;t have access to
            isn&apos;t listed.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search widgets" className="pl-9"
            data-testid="widget-picker-search" />
        </div>

        <div className="overflow-y-auto scrollbar-thin -mx-1 px-1 space-y-5 pb-1">
          {grouped.length === 0 && (
            <p className="text-sm text-fg-muted py-6" data-testid="widget-picker-empty">
              Nothing matches &ldquo;{query}&rdquo;.
            </p>
          )}
          {grouped.map(({ cat, items }) => (
            <div key={cat}>
              <p className="text-xs font-semibold uppercase tracking-wider text-fg-muted mb-2">
                {cat}
              </p>
              <div className="space-y-1.5">
                {items.map((w) => {
                  const placed = placedKeys.includes(w.widget_key);
                  return (
                    <div key={w.widget_key}
                      className="flex items-start gap-3 p-3 rounded-md border border-line
                        bg-surface hover:bg-surface-sunken transition-colors"
                      data-testid={`widget-picker-item-${w.widget_key}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-fg">{w.title}</p>
                        <p className="text-xs text-fg-muted mt-0.5">{w.description}</p>
                      </div>
                      <Button size="sm" variant={placed ? 'ghost' : 'outline'}
                        disabled={placed} onClick={() => onAdd(w.widget_key)}
                        data-testid={`widget-picker-add-${w.widget_key}`}>
                        {placed ? <><Check className="w-4 h-4 mr-1" />Added</>
                                : <><Plus className="w-4 h-4 mr-1" />Add</>}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
